import React from 'react';
import { Card, CardContent, Box, Typography, Avatar, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

/*
  MetricCard: a compact key-figure card (e.g. Income / Expenses / Balance).

  Layout is horizontal — a small tinted icon on the left, with the label and
  figure stacked tightly beside it — to keep the card short and dense rather
  than wasting vertical space.

  Props:
   - label: string (metric name)
   - value: string | node (formatted figure)
   - icon: React node
   - color: palette key for the icon tint ('success' | 'error' | 'primary' | 'warning' ...)
   - valueColor: optional palette path for the figure (defaults to `${color}.main`)
   - helper: optional small caption under the value
   - trend: optional period-over-period comparison shown inline next to the value:
       { value: number (signed %), positiveIsGood: boolean, label: string (tooltip) }
       Pass null (not undefined) when a comparison is expected but unavailable
       (e.g. $0 income on the 1st of the month): the trend slot is kept and a
       muted "—" placeholder rendered, so sibling cards stay height-aligned.
       Omit the prop entirely when trends don't apply and no slot is wanted.
   - sx: Card overrides
*/

// "2.5%" — magnitude only (always 1 dp); the arrow carries the direction.
// Huge swings off a small base ("21345%") carry no more meaning than "a lot",
// so the display caps at 999%.
const formatTrendPct = (v) => {
  const a = Math.abs(v);
  return a > 999 ? '999%+' : `${a.toFixed(1)}%`;
};

function TrendIndicator({ trend }) {
  const theme = useTheme();
  const isFlat = Math.abs(trend.value) < 0.05;
  const isUp = trend.value > 0;
  // Whether this movement is favourable for the metric (e.g. expenses falling).
  const isGood = isFlat ? null : (isUp ? trend.positiveIsGood : !trend.positiveIsGood);
  const color = isFlat
    ? theme.palette.text.secondary
    : (isGood ? theme.palette.success.main : theme.palette.error.main);
  const TrendIcon = isFlat ? TrendingFlatIcon : (isUp ? TrendingUpIcon : TrendingDownIcon);

  // Rendered as a single inline element (icon vertical-aligned within the text)
  // so it exposes a real text baseline — letting the parent row baseline-align
  // the "%" with the bottom of the much larger figure beside it.
  return (
    <Tooltip title={trend.label || ''} arrow>
      <Typography
        component="span"
        variant="caption"
        sx={{ fontWeight: 700, color, whiteSpace: 'nowrap', flexShrink: 0, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
      >
        <TrendIcon sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, verticalAlign: 'text-bottom', mr: 0.25 }} />
        {formatTrendPct(trend.value)}
      </Typography>
    </Tooltip>
  );
}

export default function MetricCard({ label, value, icon, color = 'primary', valueColor, helper, trend, filled = false, fillColor = 'primary', sx }) {
  const theme = useTheme();
  const main = theme.palette[color]?.main || theme.palette.primary.main;
  const fill = theme.palette[fillColor] || theme.palette.primary;

  // Filled variant: a solid coloured card with white text — used to make a
  // headline figure (e.g. Net Worth) stand out from the plain cards.
  const cardSx = filled
    ? { color: '#fff', background: `linear-gradient(135deg, ${fill.main} 0%, ${fill.dark} 100%)` }
    : {};
  const avatarSx = filled
    ? { bgcolor: alpha('#fff', 0.22), color: '#fff' }
    : { bgcolor: alpha(main, 0.12), color: main };
  const labelColor = filled ? alpha('#fff', 0.85) : 'text.secondary';
  const figureColor = filled ? '#fff' : (valueColor || `${color}.main`);
  const helperColor = filled ? alpha('#fff', 0.8) : 'text.secondary';

  return (
    <Card elevation={2} sx={{ height: '100%', ...cardSx, ...sx }}>
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.25, sm: 1.75 },
          p: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        {/* Icon centred against the whole text block so it spans the label,
            figure and trend lines. Smaller on phones so it doesn't squeeze
            the figure in the 2-up grid. */}
        <Avatar
          variant="rounded"
          sx={{
            ...avatarSx,
            width: { xs: 36, sm: 50 },
            height: { xs: 36, sm: 50 },
            borderRadius: 2,
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: { xs: '1.25rem', sm: '1.7rem' } },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: labelColor, fontWeight: 600, lineHeight: 1.2, mb: 0.75 }}
            noWrap
          >
            {label}
          </Typography>
          {/* Figure on its own line with the trend stacked beneath it, so a
              wide (six-figure) value keeps the full card width to itself. */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              fontWeight="bold"
              sx={{
                color: figureColor,
                fontSize: { xs: '1.35rem', sm: 'clamp(1.2rem, 2.2vw, 1.55rem)' },
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Typography>
            {trend ? (
              <Box sx={{ mt: 0.25 }}>
                <TrendIndicator trend={trend} />
              </Box>
            ) : trend === null ? (
              // Same variant/size as TrendIndicator so the placeholder row is
              // pixel-identical in height to a real trend row.
              <Box sx={{ mt: 0.25 }}>
                <Tooltip title="No comparison yet" arrow>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: 'text.disabled',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                    }}
                  >
                    —
                  </Typography>
                </Tooltip>
              </Box>
            ) : null}
          </Box>
          {helper && (
            <Typography variant="caption" noWrap sx={{ display: 'block', color: helperColor }}>
              {helper}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
